import { Award, Users, Target, Eye, Heart } from 'lucide-react';
import { useAboutInfo, useGlobalStatistiques, useEmployes, useHeroSliders } from '../hooks/usePublicApi';
import { useTexts, useSiteInfo } from '../hooks/useTexts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useState, useEffect } from 'react';

export default function AboutPage() {
  // Récupération des données dynamiques
  const { data: aboutInfo, loading: aboutLoading, error: aboutError } = useAboutInfo();
  const { data: globalStats, loading: statsLoading } = useGlobalStatistiques();
  const { data: employes, loading: employesLoading } = useEmployes();
  const { data: heroSliders, loading: heroSlidersLoading } = useHeroSliders();
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  const { getSiteInfo } = useSiteInfo();

  // Sélection d'une image aléatoire du slider hero
  const [selectedHeroImage, setSelectedHeroImage] = useState<string | null>(null);
  
  useEffect(() => {
    if (heroSliders && heroSliders.length > 0) {
      // Sélectionner une image aléatoire
      const randomIndex = Math.floor(Math.random() * heroSliders.length);
      const selectedSlide = heroSliders[randomIndex];
      setSelectedHeroImage(selectedSlide.image_url_final || null);
    } else {
      setSelectedHeroImage(null);
    }
  }, [heroSliders]);

  // Image par défaut
  const defaultHeroImage = 'https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&w=1920';
  const heroImageUrl = selectedHeroImage || defaultHeroImage;

  // Gestion des états de chargement et d'erreur
  if (aboutLoading || statsLoading || employesLoading || heroSlidersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (aboutError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorMessage 
          error={aboutError} 
          onDismiss={() => window.location.reload()} 
        />
      </div>
    );
  }

  // Fonction pour récupérer une valeur par clé
  const getInfoByKey = (key: string, defaultValue: string = '') => {
    return aboutInfo?.find(info => info.cle_info === key)?.contenu || 
           aboutInfo?.find(info => info.cle_info === key)?.valeur || 
           defaultValue;
  };

  // Récupération des contenus dynamiques avec valeurs par défaut
  const heroTitle = getInfoByKey('about_titre_principal', getText('about.hero.title'));
  const heroSubtitle = getInfoByKey('about_sous_titre', getText('about.hero.subtitle'));
  
  const histoireSection = getInfoByKey('about_histoire_titre', getText('about.story.title'));
  const histoireContenu = getInfoByKey('about_histoire_contenu', getText('about.story.content'));
  
  const missionTitre = getInfoByKey('about_mission_titre', getText('about.mission.title'));
  const missionContenu = getInfoByKey('about_mission_contenu', getText('about.mission.content'));
  
  const visionTitre = getInfoByKey('about_vision_titre', getText('about.vision.title'));
  const visionContenu = getInfoByKey('about_vision_contenu', getText('about.vision.content'));
  
  const valeursTitre = getInfoByKey('about_valeurs_titre', getText('about.values.title'));
  const valeursSousTitre = getInfoByKey('about_valeurs_sous_titre', getText('about.values.subtitle'));
  
  const equipeTitre = getInfoByKey('about_equipe_titre', getText('about.team.title'));
  const equipeSousTitre = getInfoByKey('about_equipe_sous_titre', getText('about.team.subtitle'));
  // Valeurs dynamiques
  const values = [
    {
      icon: Target,
      title: getInfoByKey('about_valeur_excellence_titre', getText('values.excellence.title')),
      description: getInfoByKey('about_valeur_excellence_desc', getText('values.excellence.description'))
    },
    {
      icon: Heart,
      title: getInfoByKey('about_valeur_integrite_titre', getText('values.integrity.title')),
      description: getInfoByKey('about_valeur_integrite_desc', getText('values.integrity.description'))
    },
    {
      icon: Users,
      title: getInfoByKey('about_valeur_partenariat_titre', getText('values.partnership.title')),
      description: getInfoByKey('about_valeur_partenariat_desc', getText('values.partnership.description'))
    },
    {
      icon: Award,
      title: getInfoByKey('about_valeur_innovation_titre', getText('values.innovation.title')),
      description: getInfoByKey('about_valeur_innovation_desc', getText('values.innovation.description'))
    }
  ];



  // Équipe
  const team = employes && employes.length > 0 ? employes.map(employe => ({
    name: employe.full_name,
    position: employe.fonction,
    image: employe.image_url || null
  })) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-blue-700 py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url(${heroImageUrl})`
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {heroTitle}
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* Stats Section */}
     {/*  <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {histoireSection}
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {histoireContenu.split('\n\n').map((paragraph, index) => (
                  <p key={index}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-w-4 aspect-h-3">
                {(() => {
                  const histoireImageInfo = getSiteInfo('about_histoire_image');
                  const histoireImageUrl = histoireImageInfo?.image_url_final;
                  const defaultImage = 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800';
                  
                  if (histoireImageUrl) {
                    const imageBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || '';
                    const fullImageUrl = histoireImageUrl.startsWith('http') ? histoireImageUrl : imageBaseUrl + histoireImageUrl;
                    return (
                      <img
                        src={fullImageUrl}
                        alt="Histoire SOGEIMCO"
                        className="w-full h-full object-cover rounded-lg shadow-lg"
                      />
                    );
                  }
                  
                  return (
                    <img
                      src={defaultImage}
                      alt="Histoire SOGEIMCO"
                      className="w-full h-full object-cover rounded-lg shadow-lg"
                    />
                  );
                })()}
              </div>
             {/*  <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-6 rounded-lg shadow-lg">
                <div className="text-2xl font-bold mb-2">15+ ans</div>
                <div className="text-blue-100">d'expertise</div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-blue-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <Target className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">{missionTitre}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {missionContenu}
              </p>
            </div>
            
            <div className="bg-teal-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <Eye className="w-8 h-8 text-teal-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">{visionTitre}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {visionContenu}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {valeursTitre}
            </h2>
            <p className="text-xl text-gray-600">
              {valeursSousTitre}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6 group-hover:bg-blue-200 transition-colors">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {equipeTitre && equipeSousTitre && team.length > 0 && (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {equipeTitre}
            </h2>
            <p className="text-xl text-gray-600">
              {equipeSousTitre}
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="text-center group w-full max-w-sm md:w-auto md:flex-shrink-0">
                <div className="relative mb-6">
                  <div className="aspect-w-1 aspect-h-1 w-48 h-48 mx-auto">
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover rounded-full shadow-lg group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full shadow-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                        <span className="text-5xl font-bold text-white">
                          {member.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-blue-600 font-medium">
                  {member.position}
                </p>
              </div>
            ))}
          </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {getText('about.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {getText('about.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/projects"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              {getText('about.cta.see_projects')}
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-colors"
            >
              {getText('about.cta.contact_us')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}